import type { SiteConfigFn, SiteLevelApi } from '@wix/thunderbolt-vsm-api-types'
import type { MultilingualSiteConfig, Language } from '@wix/thunderbolt-symbols'
import { LanguageStatus } from '@wix/multilingual-languages-utils'
import type { LanguageInfoRenderer, MultilingualInfoRenderer } from '@wix/thunderbolt-ssr-api'
import type { CalcViewerModelFunctionLibrary } from 'thunderbolt-viewer-model-root'

const LANGUAGE_SELECTOR_TYPE = 'wysiwyg.viewer.components.LanguageSelector'

/**
 * Check if language selector component exists on the site
 */
function hasLanguageSelector(componentsCountHints: Record<string, number> | undefined): boolean {
	if (!componentsCountHints) {
		return true
	}
	return !!componentsCountHints[LANGUAGE_SELECTOR_TYPE]
}

/**
 * Enrich language data with human-readable names
 */
function enrichLanguageData(
	funcLib: CalcViewerModelFunctionLibrary,
	data: LanguageInfoRenderer | undefined,
	isPrimaryLanguage: boolean
): Language | null {
	if (!data) {
		return null
	}

	const languageCode = data.languageCode
	const languageName = funcLib.mapLanguageCodeToName(languageCode)
	const localizedName = funcLib.mapLanguageCodeToLocalizedName(languageCode)

	return {
		...data,
		name: languageName,
		seoLang: data.locale,
		localizedName,
		isPrimaryLanguage,
		visitorPrimary: data.visitorPrimary,
	}
}

/**
 * Collect and enrich multilingual information from the renderer model.
 * This helper is exported and used by other features (link-utils-service, etc.)
 */
export function collectMultilingualInfo(
	api: SiteLevelApi,
	funcLib: CalcViewerModelFunctionLibrary
): {
	originalLanguage: Language
	isOriginalLanguage: boolean
	currentLanguage: Language
	siteLanguages: Language[]
	hasLanguageSelector: boolean
} {
	const sitePropertiesInfo = api.rendererModel.getSitePropertiesInfo()
	const multilingualInfoRenderer: MultilingualInfoRenderer | undefined = sitePropertiesInfo?.multilingualInfo

	// Default empty original language
	const emptyOriginalLanguage: LanguageInfoRenderer = {
		languageCode: '',
		seoLang: '',
		locale: '',
		countryCode: '',
		resolutionMethod: 'QueryParam',
		url: '',
	}

	// Get original language data, checking for empty object
	const originalLanguageData =
		multilingualInfoRenderer?.originalLanguage && Object.keys(multilingualInfoRenderer.originalLanguage).length > 0
			? multilingualInfoRenderer.originalLanguage
			: emptyOriginalLanguage

	const originalLanguage = enrichLanguageData(funcLib, originalLanguageData, true)!

	// Enrich translation languages
	const translationLanguages = multilingualInfoRenderer?.translationLanguages ?? []
	const enrichedTranslations = translationLanguages
		.map((data) => enrichLanguageData(funcLib, data, false))
		.filter((lang): lang is Language => lang !== null)

	// Combine all languages
	const allLoadedLanguages = [...enrichedTranslations, originalLanguage].filter((lang) => lang && lang.languageCode)

	// Find current language
	const currentLanguageCode = multilingualInfoRenderer?.languageCode
	const currentLanguage = currentLanguageCode
		? (allLoadedLanguages.find((lang) => lang.languageCode === currentLanguageCode) ?? originalLanguage)
		: originalLanguage

	const isOriginalLanguage = originalLanguage.languageCode === currentLanguage.languageCode

	// Filter out deleted languages and ensure all have status
	const siteLanguages = allLoadedLanguages
		.filter((lang) => lang.status !== LanguageStatus.DELETED)
		.map((lang) => ({
			...lang,
			status: lang.status || LanguageStatus.ACTIVE,
		}))

	const componentsCountHints = api.rendererModel.getSiteMetaData()?.renderHints?.componentsCount

	return {
		originalLanguage,
		isOriginalLanguage,
		currentLanguage,
		siteLanguages,
		hasLanguageSelector: hasLanguageSelector(componentsCountHints),
	}
}

const multilingualSiteConfig: SiteConfigFn<MultilingualSiteConfig> = ({ api, funcLib }) => {
	// Check if multilingual is enabled
	const multilingualInfo = api.rendererModel.getSitePropertiesInfo()?.multilingualInfo
	const isEnabled = !!multilingualInfo?.originalLanguage?.countryCode

	if (!isEnabled) {
		return {
			shouldLoadSiteFeature: false,
			shouldLoadSiteConfig: false,
			siteConfig: null,
		}
	}

	const collectedInfo = collectMultilingualInfo(api, funcLib)

	const baseUrl = api.publicModel.getExternalBaseUrl()
	const domain = api.publicModel.getDomain()
	const premiumFeatures = api.rendererModel.getPremiumFeatures() ?? []
	const isPremiumDomain = premiumFeatures.includes('HasDomain')

	const serviceTopology = api.rootConfig.getServiceTopology()
	const flagsUrl = serviceTopology?.scriptsLocationMap?.['linguist-flags'] ?? ''

	const siteConfig: MultilingualSiteConfig = {
		...collectedInfo,
		isEnabled,
		baseUrl,
		domain,
		isPremiumDomain,
		flagsUrl,
	}

	return {
		shouldLoadSiteFeature: true,
		shouldLoadSiteConfig: true,
		siteConfig,
	}
}

const name = 'multilingual'

export const config = { site: multilingualSiteConfig, name }
